import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PrismMark } from "@/components/auth/PrismMark";
import { getMe, setupPassword } from "@/api/auth";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "At least 8 characters")
      .max(128, "Too long"),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  });

type FormValues = z.infer<typeof schema>;

type LinkState = "ok" | "missing" | "expired" | "invalid";

export default function SetupPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [showPassword, setShowPassword] = useState(false);
  const [linkState, setLinkState] = useState<LinkState>("ok");

  const token = useMemo(() => searchParams.get("token") ?? "", [searchParams]);

  useEffect(() => {
    if (!token) setLinkState("missing");
  }, [token]);

  useEffect(() => {
    // If already authenticated, send them home — the link is for newcomers.
    if (isAuthenticated) navigate("/projects", { replace: true });
  }, [isAuthenticated, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  async function onSubmit(values: FormValues) {
    if (!token) {
      setLinkState("missing");
      return;
    }
    try {
      const tokens = await setupPassword({
        token,
        password: values.password,
      });
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
      });
      const me = await getMe();
      setUser(me);
      toast.success(`Welcome to Prism, ${me.full_name}`);
      navigate("/projects", { replace: true });
    } catch (err) {
      const next = classifyError(err);
      if (next === "expired" || next === "invalid") {
        setLinkState(next);
      } else {
        toast.error(extractErrorMessage(err));
      }
    }
  }

  return (
    <div className="flex min-h-screen bg-white">
      <div
        className="relative hidden flex-col items-center justify-between overflow-hidden p-10 md:flex md:w-1/2"
        style={{
          background:
            "linear-gradient(135deg, #0f0f14 0%, #1a1a2e 60%, #1f1d40 100%)",
        }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-50">
          <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-indigo-600/30 mix-blend-screen blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-violet-500/20 mix-blend-screen blur-[100px]" />
          <div className="absolute left-1/2 top-1/2 h-64 w-64 rounded-full bg-cyan-300/10 mix-blend-screen blur-[80px]" />
        </div>

        <div className="z-10 flex flex-1 flex-col items-center justify-center text-center">
          <PrismMark size={128} className="mb-8 text-white/80" />
          <h1 className="mb-3 text-[28px] font-light uppercase tracking-[0.25em] text-white">
            Prism
          </h1>
          <p className="text-sm font-light tracking-wide text-white/40">
            See what others don&rsquo;t.
          </p>
        </div>

        <div className="z-10 text-center">
          <p className="text-[11px] font-light uppercase tracking-wider text-white/20">
            B2B Lead Engine · Enterprise
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 p-8 md:w-1/2">
        <div className="w-full max-w-[380px]">
          <div className="mb-10">
            <h2 className="mb-2 text-3xl font-semibold tracking-tight text-slate-900">
              Create your password
            </h2>
            <p className="text-[15px] font-light text-slate-500">
              You&rsquo;ve been invited to join Prism.
            </p>
          </div>

          {linkState !== "ok" ? (
            <BrokenLinkState state={linkState} />
          ) : (
            <form
              className="space-y-5"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-700"
                >
                  New password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    autoFocus
                    aria-invalid={!!errors.password}
                    className={cn(
                      "h-11 rounded-xl border-slate-200 bg-white pr-11",
                      errors.password &&
                        "border-rose-400 focus-visible:ring-rose-300",
                    )}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 transition-colors hover:text-slate-700"
                    tabIndex={-1}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-rose-600">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirm"
                  className="block text-sm font-medium text-slate-700"
                >
                  Confirm password
                </label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  aria-invalid={!!errors.confirm}
                  className={cn(
                    "h-11 rounded-xl border-slate-200 bg-white",
                    errors.confirm &&
                      "border-rose-400 focus-visible:ring-rose-300",
                  )}
                  {...register("confirm")}
                />
                {errors.confirm && (
                  <p className="text-xs text-rose-600">
                    {errors.confirm.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="h-11 w-full rounded-xl text-sm font-medium transition-all hover:opacity-95 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Activating account…
                  </>
                ) : (
                  "Activate my account"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function BrokenLinkState({ state }: { state: LinkState }) {
  const navigate = useNavigate();
  const message =
    state === "expired"
      ? "This link has expired. Ask your administrator for a new one."
      : state === "missing"
        ? "Missing token. Open the link from your invitation email."
        : "Invalid link. Ask your administrator for a new one.";

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/60 p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
        <div>
          <p className="text-sm font-medium text-rose-700">
            We couldn&rsquo;t verify your invitation
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-rose-600">
            {message}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        className="h-11 w-full rounded-xl"
        onClick={() => navigate("/login")}
      >
        Back to sign-in
      </Button>
    </div>
  );
}

function classifyError(err: unknown): LinkState | "other" {
  if (!axios.isAxiosError(err)) return "other";
  const status = err.response?.status;
  const data = err.response?.data as
    | { detail?: unknown; code?: unknown }
    | undefined;
  const detail =
    typeof data?.detail === "string" ? data.detail.toLowerCase() : "";
  const code = typeof data?.code === "string" ? data.code.toLowerCase() : "";

  if (
    status === 410 ||
    code.includes("expired") ||
    detail.includes("expired")
  ) {
    return "expired";
  }
  if (
    status === 400 ||
    status === 404 ||
    status === 422 ||
    code.includes("invalid") ||
    detail.includes("invalid")
  ) {
    return "invalid";
  }
  return "other";
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    if (err.code === "ERR_NETWORK") {
      return "Cannot reach the server.";
    }
  }
  return "Couldn't activate your account.";
}
